import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarOff } from 'lucide-react';
import { LEAVE_TYPES, type LeaveRequest, type LeaveType } from '@tailoring-crm/shared';
import {
  useAskForLeave,
  useCancelLeaveRequest,
  useDecideLeaveRequest,
  useLeaveRequests,
} from '../../lib/attendance/use-attendance.js';
import { EmptyState } from '../feedback/empty-state.js';
import { Badge, Button, Input, Label, ListSkeleton, useToast } from '@tailoring-crm/ui';

/**
 * Asking for days off, and answering.
 *
 * ONE PANEL FOR BOTH SIDES, because they are the same list read by two people. A karigar sees
 * the requests they filed and can withdraw one that is still waiting; an owner sees everybody's
 * and can answer. Building two screens would have meant two places for the status vocabulary to
 * drift, and the API already narrows the list by who is asking.
 *
 * The approval button says what it will DO — "approve and mark 3 days" — because that is the
 * whole point of asking through the app: the roster is marked in the same act, and an owner who
 * does not know that will go and mark it again by hand.
 */

export interface LeaveRequestsPanelProps {
  /** True for an owner or manager: they may answer, and they see everybody's. */
  canDecide: boolean;
}

export function LeaveRequestsPanel({ canDecide }: LeaveRequestsPanelProps): ReactElement {
  const { t } = useTranslation();
  const [pendingOnly, setPendingOnly] = useState(true);
  const [asking, setAsking] = useState(false);

  const requests = useLeaveRequests({ pendingOnly });
  const items = requests.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={pendingOnly}
            onChange={(event) => setPendingOnly(event.target.checked)}
          />
          {t('attendance.leaveRequests.pendingOnly')}
          {requests.data && requests.data.pendingCount > 0 ? (
            <Badge variant="secondary">{requests.data.pendingCount}</Badge>
          ) : null}
        </label>
        <Button onClick={() => setAsking(!asking)}>{t('attendance.leaveRequests.ask')}</Button>
      </div>

      {asking ? <AskForLeaveForm onDone={() => setAsking(false)} /> : null}

      {requests.isPending ? <ListSkeleton rows={3} /> : null}

      {!requests.isPending && items.length === 0 ? (
        <EmptyState
          icon={CalendarOff}
          title={t('attendance.leaveRequests.emptyTitle')}
          description={t('attendance.leaveRequests.emptyDescription')}
        />
      ) : null}

      {items.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {items.map((request) => (
            <LeaveRequestRow key={request.id} request={request} canDecide={canDecide} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function LeaveRequestRow({
  request,
  canDecide,
}: {
  request: LeaveRequest;
  canDecide: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const decide = useDecideLeaveRequest();
  const cancel = useCancelLeaveRequest();
  const [note, setNote] = useState('');

  const answer = (approve: boolean): void => {
    void (async () => {
      try {
        const result = await decide.mutateAsync({
          requestId: request.id,
          approve,
          note: note.trim() || null,
        });
        toast({
          // The message says what happened to the ROSTER, including the days that were left
          // alone because somebody had already worked them — otherwise an owner is left
          // wondering whether the approval took.
          title: approve
            ? t('attendance.leaveRequests.approved', {
                marked: result.daysMarked.length,
                skipped: result.daysSkipped.length,
              })
            : t('attendance.leaveRequests.rejected'),
          variant: 'success',
        });
      } catch (error) {
        toast({
          title: error instanceof Error ? error.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <li className="flex flex-col gap-2 rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{request.staffName}</p>
          <p className="text-sm text-muted-foreground">
            {request.fromDay === request.toDay
              ? request.fromDay
              : `${request.fromDay} → ${request.toDay}`}{' '}
            · {t('attendance.leaveRequests.dayCount', { count: request.dayCount })} ·{' '}
            {t(`attendance.leaveRequests.type.${request.type}`)}
          </p>
          <p className="text-sm">{request.reason}</p>
          {request.decisionNote ? (
            <p className="text-sm text-muted-foreground">
              {t('attendance.leaveRequests.note', { note: request.decisionNote })}
            </p>
          ) : null}
        </div>
        <Badge variant={request.status === 'pending' ? 'default' : 'secondary'}>
          {t(`attendance.leaveRequests.status.${request.status}`)}
        </Badge>
      </div>

      {request.status === 'pending' ? (
        <div className="flex flex-col gap-2">
          {canDecide ? (
            <>
              <Input
                value={note}
                maxLength={300}
                placeholder={t('attendance.leaveRequests.notePlaceholder')}
                aria-label={t('attendance.leaveRequests.notePlaceholder')}
                onChange={(event) => setNote(event.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" disabled={decide.isPending} onClick={() => answer(true)}>
                  {t('attendance.leaveRequests.approveAndMark', { count: request.dayCount })}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decide.isPending}
                  onClick={() => answer(false)}
                >
                  {t('attendance.leaveRequests.reject')}
                </Button>
              </div>
            </>
          ) : (
            <div>
              <Button
                size="sm"
                variant="ghost"
                disabled={cancel.isPending}
                onClick={() => void cancel.mutateAsync(request.id)}
              >
                {t('attendance.leaveRequests.withdraw')}
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </li>
  );
}

function AskForLeaveForm({ onDone }: { onDone: () => void }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const ask = useAskForLeave();

  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(today);
  const [toDay, setToDay] = useState(today);
  const [type, setType] = useState<LeaveType>('casual');
  const [reason, setReason] = useState('');

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      try {
        // No `staffUserId`: the API resolves it from the session for anybody who may only file
        // their own, so naming somebody else is a refusal rather than a request against them.
        await ask.mutateAsync({ fromDay, toDay, type, reason });
        toast({ title: t('attendance.leaveRequests.asked'), variant: 'success' });
        onDone();
      } catch (error) {
        toast({
          title: error instanceof Error ? error.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <form className="flex flex-col gap-3 rounded-md border border-border p-3" onSubmit={submit}>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="leave-from">{t('attendance.leaveRequests.from')}</Label>
          <Input
            id="leave-from"
            type="date"
            value={fromDay}
            onChange={(event) => {
              setFromDay(event.target.value);
              // A single day is the common case, so the second date follows the first until
              // somebody deliberately moves it past.
              if (event.target.value > toDay) setToDay(event.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="leave-to">{t('attendance.leaveRequests.to')}</Label>
          <Input
            id="leave-to"
            type="date"
            value={toDay}
            min={fromDay}
            onChange={(event) => setToDay(event.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="leave-type">{t('attendance.leaveRequests.typeLabel')}</Label>
        <select
          id="leave-type"
          value={type}
          onChange={(event) => setType(event.target.value as LeaveType)}
          className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
        >
          {LEAVE_TYPES.map((one) => (
            <option key={one} value={one}>
              {t(`attendance.leaveRequests.type.${one}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="leave-reason">{t('attendance.leaveRequests.reason')}</Label>
        <Input
          id="leave-reason"
          value={reason}
          required
          maxLength={300}
          placeholder={t('attendance.leaveRequests.reasonPlaceholder')}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={ask.isPending}>
          {t('attendance.leaveRequests.send')}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

export default LeaveRequestsPanel;
